import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { ChatService } from '../../../services/chat.service';
import { AIConversationService, Question, ContentPreviewResponse } from '../../../services/ai-conversation.service';
import { ChatCrewService, ChatCrewMessage } from '../../../services/chat-crew.service';
import { ChatMessage } from '../../../interfaces/chat-message.interface';
import { ChatPhase, AIContentGeneration } from '../../../interfaces/ai-content.interface';
import { COLOR_THEMES } from '../../../models/color-theme.model';
import { IndexedDBService } from '../../../services/indexeddb.service';
import { ChangeRequestService } from '../../../services/change-request.service';
import { PreviewService } from '../../../services/preview.service';

@Component({
  selector: 'app-otto-chat',
  imports: [CommonModule, FormsModule],
  templateUrl: './otto-chat.component.html',
  styleUrls: ['./otto-chat.component.scss']
})
export class OttoChatComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('chatMessages') chatMessagesElement!: ElementRef<HTMLDivElement>;
  @ViewChild('chatInput') chatInputElement!: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();

  messages: ChatMessage[] = [];
  isMinimized: boolean = false;
  userInput: string = '';

  formData: any = {};
  selectedFile: File | null = null;
  filePreviewUrl: string | null = null;
  isDragOver: boolean = false;
  isDeploying: boolean = false;
  selectedCapabilitiesForQuestion: string[] = [];
  
  currentPhase: ChatPhase = ChatPhase.DATA_COLLECTION;
  isGeneratingAIContent: boolean = false;
  aiContentGeneration: AIContentGeneration | null = null;
  
  currentAIQuestion: Question | null = null;
  contentPreview: ContentPreviewResponse | null = null;
  isProcessingAI: boolean = false;
  aiConfidence: number = 0;
  showFollowUpHints: boolean = false;
  // AI Mode is now permanently enabled - this should always be true
  isAIModeEnabled: boolean = false; // AI mode starts disabled, enabled after theme selection
  aiControlEnabled: boolean = false; // Whether AI is currently controlling the conversation flow
  isTyping: boolean = false; // Typing indicator for AI responses
  isProcessingMessage: boolean = false; // Flag to prevent duplicate message sends
  useChatCrew: boolean = true; // Enable ChatCrew for YAML-based conversation flow
  useChangeRequestMode: boolean = false; // Use new change request system in deployment phase
  

  
  // Chat initialization loading state
  isChatLoading: boolean = true; // Whether chat is still initializing
  private autoSaveStarted: boolean = false; // Track if auto-save has been started
  private aiInitializationStarted: boolean = false; // Track if AI initialization has been started
  
  /**
   * Check if chat component is fully ready (not loading and AI initialized if needed)
   */
  public isChatReady(): boolean {
    return !this.isChatLoading && (!this.aiControlEnabled || this.aiInitializationStarted);
  }
  
  colorThemes = COLOR_THEMES;
  ChatPhase = ChatPhase;

  capabilitiesOptions: string[] = [
    '3D Printing',
    'Blow Molding',
    'Compression Molding',
    'Die Casting',
    'Electro-Mechanical Assembly',
    'Engineering & Design',
    'Extrusion',
    'Forging',
    'Gears',
    'Injection Molding',
    'Investment Casting',
    'Machining',
    'Screw/Switch machine',
    'Rotational Molding',
    'RTV Molding',
    'Sand Molding Casting',
    'Sheet Metal Fabrication',
    'Laser Cutting',
    'Waterjet Cutting',
    'Stamping',
    'StructuralFoam Molding',
    'Thermoforming',
    'Tool, Die & Mold Making',
    'Tube Modification',
    'Wire Harness',
    'Woodworking'
  ];

  // Company GUID will be obtained from chat service (from query parameter)
  private hasRestoredSession: boolean = false;
  
  private getCompanyGuid(): string {
    return this.chatService.getCompanyGuid();
  }

  constructor(
    public chatService: ChatService,
    private aiConversationService: AIConversationService,
    private chatCrewService: ChatCrewService,
    private indexedDBService: IndexedDBService,
    private changeRequestService: ChangeRequestService,
    private previewService: PreviewService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeChat();
      
      // Auto-save will be started after first user interaction
      
          // Hide loader after initialization (reduced delay for faster tour display)
    setTimeout(() => {
      this.isChatLoading = false;
    }, 100);
    } else {
      this.isChatLoading = false;
    }
    
    // Subscribe to messages (no auto-save)
    this.chatService.messages$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async messages => {
        this.messages = messages;
        setTimeout(() => this.scrollToBottom(), 100);
      });



    this.chatService.isMinimized$
      .pipe(takeUntil(this.destroy$))
      .subscribe(minimized => {
        this.isMinimized = minimized;
      });

    this.chatService.isDeploying$
      .pipe(takeUntil(this.destroy$))
      .subscribe(deploying => {
        this.isDeploying = deploying;
        if (deploying) {
          this.deploymentStartTime = Date.now();
          this.startProgressTimer();
        } else {
          this.stopProgressTimer();
        }
      });

    this.chatService.currentPhase$
      .pipe(takeUntil(this.destroy$))
      .subscribe(phase => {
        this.currentPhase = phase;
        
        // Color selection phase is now handled in preview component
        
        // Auto-enable change request mode when deployment phase is reached
        if (phase === ChatPhase.DEPLOYMENT && !this.useChangeRequestMode) {
          this.useChangeRequestMode = true;
          this.useChatCrew = false; // Disable ChatCrew in deployment phase
          this.isAIModeEnabled = true;
          this.aiControlEnabled = true;
          this.chatService.enableAIControl();
          this.currentAIQuestion = null;
          
          // Stop any ongoing AI conversation services
          this.aiConversationService.resetFallbackMode();
        }
      });

    this.chatService.isGeneratingAIContent$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isGenerating => {
        this.isGeneratingAIContent = isGenerating;
      });

    this.chatService.aiContentGeneration$
      .pipe(takeUntil(this.destroy$))
      .subscribe(generation => {
        this.aiContentGeneration = generation;
        
        // CRITICAL FIX: Transition to deployment phase after content generation completes
        if (generation.status === 'completed') {
          
          // Disable AI control since data collection and content generation are complete
          this.chatService.disableAIControl();
          
          // Transition to deployment phase
          setTimeout(() => {
            this.currentPhase = ChatPhase.DEPLOYMENT;
            (this.chatService as any).currentPhaseSubject.next(ChatPhase.DEPLOYMENT);
            
            // Add deployment ready message
            this.chatService.addMessage(`
              <div style="background: linear-gradient(135deg, #28a745, #20c997); color: white; border-radius: 15px; padding: 1.5rem; text-align: center; margin: 1rem 0;">
                <div style="font-size: 1.2rem; font-weight: bold; margin-bottom: 0.5rem;">
                  🎉 All Set! Your Website is Ready
                </div>
                <div style="font-size: 0.9rem; opacity: 0.9;">
                  I've collected all the necessary information and generated your content. You can now deploy your website or make any changes if needed.
                </div>
              </div>
            `);
          }, 500);
        }
      });

    // Step 2.3: Subscribe to AI Conversation Service state
    this.aiConversationService.currentQuestion$
      .pipe(takeUntil(this.destroy$))
      .subscribe(question => {
        this.currentAIQuestion = question;
        this.showFollowUpHints = !!(question?.followUpHints?.length);
      });

    this.aiConversationService.contentPreview$
      .pipe(takeUntil(this.destroy$))
      .subscribe(preview => {
        this.contentPreview = preview;
      });

    this.aiConversationService.isProcessing$
      .pipe(takeUntil(this.destroy$))
      .subscribe(processing => {
        this.isProcessingAI = processing;
      });



    this.chatService.aiControlEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async aiControlEnabled => {
        this.aiControlEnabled = aiControlEnabled;
        
        if (aiControlEnabled && !this.isAIModeEnabled) {
          this.isAIModeEnabled = true;
          
          // Show typing indicator during AI initialization
          this.isTyping = true;
          this.scrollToBottom();
          
          // Add transition message
          // await this.chatService.addMessage('Perfect! Now I\'ll ask you some questions to learn more about your business...');
          // await this.sleep(500); // Small delay for better UX
        }
        
        if (aiControlEnabled) {
          // Color selection is now handled in preview component
        }
        
        // When AI control is enabled, initialize the AI conversation
        // BUT skip initialization for restored sessions AND deployment phase to prevent duplicate questions
        if (aiControlEnabled && !this.currentAIQuestion && !this.hasRestoredSession && this.currentPhase !== ChatPhase.DEPLOYMENT && !this.aiInitializationStarted) {
          this.aiInitializationStarted = true; // Prevent multiple initializations
          
          try {
            // FIX: Wait for chat service to complete initialization
            // Give the chat service time to restore messages from IndexedDB
            await this.sleep(200);
            
            // Now check if we have messages
            const existingMessages = await firstValueFrom(this.chatService.messages$);
            console.log('🔍 AI Initialization - Messages after wait:', existingMessages.length, existingMessages);
            
            // Proceed with AI initialization
            await this.initializeAIServiceWithCollectedData();
            
          } catch (error) {
            console.error('🔧 AI initialization failed:', error);
            this.aiInitializationStarted = false; // Reset on error to allow retry
          } finally {
            // Hide typing indicator after initialization (success or failure)
            this.isTyping = false;
          }
        } else if (aiControlEnabled && (this.hasRestoredSession || this.currentPhase === ChatPhase.DEPLOYMENT)) {
          this.isTyping = false; // Ensure typing indicator is off
        } else if (aiControlEnabled && this.aiInitializationStarted) {
          this.isTyping = false;
        } else if (aiControlEnabled && this.currentAIQuestion) {
          this.isTyping = false;
        }
      });
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
    this.setupScrollHandling();
  }

  private setupScrollHandling(): void {
    // Ensure proper scroll handling for forms
    if (this.chatMessagesElement) {
      const messagesElement = this.chatMessagesElement.nativeElement;
      
      // Add passive event listeners to prevent scroll blocking
      messagesElement.addEventListener('wheel', (e) => {
        // Allow scrolling to work properly even when forms are present
        if (messagesElement.scrollHeight > messagesElement.clientHeight) {
          e.stopPropagation();
        }
      }, { passive: true });
      
      messagesElement.addEventListener('touchmove', (e) => {
        // Allow touch scrolling to work properly
        if (messagesElement.scrollHeight > messagesElement.clientHeight) {
          e.stopPropagation();
        }
      }, { passive: true });
    }
  }

  ngOnDestroy(): void {
    this.stopProgressTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onKeyPress(event: KeyboardEvent): void {
    // Only handle Enter key on keydown to prevent double-firing
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevent form submission
      event.stopPropagation(); // Stop event bubbling
      this.sendMessage();
    }
  }

  async sendMessage(): Promise<void> {
    
    // Prevent duplicate sends
    if (!this.userInput.trim() || this.isProcessingMessage) {
      return;
    }
    

    // Set processing flag
    this.isProcessingMessage = true;

    const input = this.userInput.trim();
    this.userInput = '';

    // AUTO-SAVE LOGIC MOVED HERE - executes for every message regardless of flow
    
    // Start auto-save after first user interaction
    if (!this.autoSaveStarted) {
      this.chatService.startAutoSave();
      this.autoSaveStarted = true;
    }

    // Enhanced flow: Check if AI should control the conversation OR if we're in deployment phase

    if ((this.isAIModeEnabled && this.aiControlEnabled) || this.currentPhase === ChatPhase.DEPLOYMENT) {
      try {
        // Check if ChatCrew should be used for data collection phase only
        if (this.useChatCrew && !this.useChangeRequestMode && this.currentPhase !== ChatPhase.DEPLOYMENT) {
          await this.processChatCrewMessage(input);
          // Reset processing flag before returning
          this.isProcessingMessage = false;
          return;
        }
        
        // Special handling for deployment phase - use new change request system
        if (this.useChangeRequestMode || this.currentPhase === ChatPhase.DEPLOYMENT) {
          await this.processChangeRequest(input);
          // Reset processing flag before returning
          this.isProcessingMessage = false;
          return;
        }
        
        // AI is in control - use AI conversation service
        
        // Add user message to chat first
        await this.chatService.addMessage(input, false);
        
        // Save the latest conversation pair immediately after user response
        await this.saveLatestChatPair();
        
        // Show typing indicator
        this.isTyping = true;
        this.scrollToBottom();
        
        // Ensure AI service has the latest data from traditional flow
        await this.syncDataToAIService();
        
        // Process with AI service
        const result = await this.aiConversationService.processUserResponse(input);
        
        // Hide typing indicator
        this.isTyping = false;
        
        if (result && result.success) {
          let aiResponse = result.nextQuestion || '';
          
          if (result.extractedData && Object.keys(result.extractedData).length > 0) {
            const currentData = this.chatService.getSupplierData();
            const updatedData = { ...currentData, ...result.extractedData };
            
            // Update the chat service's supplier data
            this.chatService['supplierDataSubject'].next(updatedData);
            
            // Store supplier data to IndexedDB
            await this.indexedDBService.setUserSupplierData(this.chatService.getCompanyGuid(), updatedData);
            
            // Trigger preview update with the new data
            await this.chatService['updatePreview']();
          }
          
          // Check if AI processing indicates completion/termination
          if ((result.phase === 'deployment' || result.phase === 'ai_generation') && !result.nextQuestion) {
            await this.handleAITermination();
            // Reset processing flag before returning
            this.isProcessingMessage = false;
            return;
          }
          
          // CRITICAL FIX: Enhanced manual termination detection 
          // Check for completion signals in the 6 response
          if (aiResponse) {
            const completionSignals = [
              'perfect! i have all the information',
              'i have all the information needed',
              'all the essential information',
              'website is ready',
              'ready to create your website',
              'collected all the necessary',
              'have everything i need'
            ];
            
            const responseText = aiResponse.toLowerCase();
            const hasCompletionSignal = completionSignals.some(signal => 
              responseText.includes(signal)
            );
            
            if (hasCompletionSignal) {
              // Add small delay to ensure the AI response is processed first
              setTimeout(() => {
                this.currentPhase = ChatPhase.DEPLOYMENT;
                (this.chatService as any).currentPhaseSubject.next(ChatPhase.DEPLOYMENT);
              }, 1000);
              // Reset processing flag before returning
              this.isProcessingMessage = false;
              return;
            }
          }
          
          // Original manual termination detection as fallback
          if (!result.nextQuestion || result.nextQuestion.trim() === '') {
            const supplierData = this.chatService.getSupplierData();
            const hasEssentialData = supplierData.companyName && supplierData.contactEmail && supplierData.aboutContent;
            
            if (hasEssentialData) {
              this.currentPhase = ChatPhase.DEPLOYMENT;
              (this.chatService as any).currentPhaseSubject.next(ChatPhase.DEPLOYMENT);
              // Reset processing flag before returning
              this.isProcessingMessage = false;
              return;
            }
          }
          
          if (aiResponse && aiResponse.trim()) {
            // AI processing succeeded - add AI response and update state
            await this.chatService.addMessage(aiResponse);
            this.aiConfidence = result.confidence || 0;
            
            // Update the current AI question state for the UI
            this.currentAIQuestion = {
              text: aiResponse,
              type: 'contextual',
              expectedFields: [],
              priority: 1,
              followUpHints: [],
              questionType: 'contextual',
              confidence: result.confidence || 0.5
            };
            
            // Reset fallback mode since AI is working again
            this.aiConversationService.resetFallbackMode();
          } else {
            console.warn('AI returned empty response, falling back to traditional flow');
            this.chatService.disableAIControl();
            this.currentAIQuestion = null;
            await this.chatService.handleUserInput(input);
          }
          
        } else {
          console.warn('AI processing failed, disabling AI control and falling back to traditional flow');
          // AI processing failed - fallback to traditional flow
          this.chatService.disableAIControl();
          this.currentAIQuestion = null;
          await this.chatService.handleUserInput(input);
        }
        
      } catch (error) {
        console.error('❌ AI conversation error:', error);
        // Hide typing indicator on error
        this.isTyping = false;
        // Fallback to traditional flow on exception
        console.warn('AI conversation error, disabling AI control and falling back to traditional flow');
        this.chatService.disableAIControl();
        this.currentAIQuestion = null;
        await this.chatService.handleUserInput(input);
      }
    } else {
      // Traditional flow - use traditional chat service (theme selection or AI disabled)
      this.currentAIQuestion = null;
      await this.chatService.handleUserInput(input);
      
      // Save the latest conversation pair immediately after user response
      await this.saveLatestChatPair();
    }

    // Reset processing flag
    this.isProcessingMessage = false;
  }

  /**
   * Process message using ChatCrew (YAML-based conversation flow)
   */
  private async processChatCrewMessage(input: string): Promise<void> {
    try {
      await this.chatService.addMessage(input, false);
      
      // Save the latest conversation pair immediately after user response
      await this.saveLatestChatPair();
      
      this.isTyping = true;
      this.scrollToBottom();
      
      const messages = this.messages.map(msg => ({
        role: msg.isBot ? 'assistant' : 'user',
        content: msg.content,
        timestamp: msg.timestamp?.toISOString() || new Date().toISOString()
      }));
      
      const response = await this.chatCrewService.processConversation(messages).toPromise();
      
      this.isTyping = false;
      
      if (response && response.status === 'success' && response.response) {
        if (response.response.includes('COMPLETION_SIGNAL:')) {
          const actualResponse = response.response.replace('COMPLETION_SIGNAL123456:', '').trim();
          await this.chatService.addMessage(actualResponse);
          
          this.aiControlEnabled = false;
          this.currentAIQuestion = null;
          this.chatService.disableAIControl();
          
          this.currentPhase = ChatPhase.DEPLOYMENT;
          (this.chatService as any).currentPhaseSubject.next(ChatPhase.DEPLOYMENT);
          return;
        }
        
        let parsedResponse;
        try {
          const cleanedResponse = this.extractJsonFromResponse(response.response);
          parsedResponse = JSON.parse(cleanedResponse);
        } catch (parseError) {
          await this.chatService.addMessage(response.response);
          await this.extractAndSyncConversationData();
        
          this.currentAIQuestion = {
            text: response.response,
            type: 'contextual',
            expectedFields: [],
            priority: 1,
            followUpHints: [],
            questionType: 'contextual',
            confidence: 0.8
          };
          return;
        }
        
        const { message, extracted_data, change_request, completion_status, next_missing_field } = parsedResponse;
        
        if (change_request && change_request.field && change_request.new_value) {
          const currentData = this.chatService.getSupplierData();
          const updatedData = { ...currentData };
          
          Object.assign(updatedData, { [change_request.field]: change_request.new_value });
          
          (this.chatService as any).supplierDataSubject.next(updatedData);
          
          // Store supplier data to IndexedDB
          await this.indexedDBService.setUserSupplierData(this.getCompanyGuid(), updatedData);
          
          // Update preview
          await (this.chatService as any).updatePreview();
          

        }
        
        if (extracted_data && Object.keys(extracted_data).length > 0) {
          const currentData = this.chatService.getSupplierData();
          const updatedData = { ...currentData };
          
          Object.keys(extracted_data).forEach(key => {
            if (extracted_data[key] && extracted_data[key].trim() !== '') {
              (updatedData as any)[key] = extracted_data[key];
            }
          });
          
          (this.chatService as any).supplierDataSubject.next(updatedData);
          
          // Store supplier data to IndexedDB
          await this.indexedDBService.setUserSupplierData(this.getCompanyGuid(), updatedData);
          
          // Update preview
          await (this.chatService as any).updatePreview();
          

        }
        
        if (completion_status === 'complete') {
          await this.chatService.addMessage(message);
          this.aiControlEnabled = false;
          this.currentAIQuestion = null;
          this.chatService.disableAIControl();
          this.chatService.triggerAIContentGeneration();
          this.currentPhase = ChatPhase.DEPLOYMENT;
          (this.chatService as any).currentPhaseSubject.next(ChatPhase.DEPLOYMENT);
          return;
        }
        
        await this.chatService.addMessage(message);
        
        this.currentAIQuestion = {
          text: message,
          type: 'contextual',
          expectedFields: next_missing_field ? [next_missing_field] : [],
          priority: 1,
          followUpHints: [],
          questionType: 'contextual',
          confidence: 0.9
        };
        
      } else {
        this.chatService.disableAIControl();
        this.currentAIQuestion = null;
        await this.chatService.handleUserInput(input);
      }
      
    } catch (error) {
      this.isTyping = false;
      
      this.chatService.disableAIControl();
      this.currentAIQuestion = null;
      await this.chatService.handleUserInput(input);
    } finally {
      this.isProcessingMessage = false;
    }
  }

  /**
   * Initialize AI service with any data collected from traditional flow
   */
  private async initializeAIServiceWithCollectedData(): Promise<void> {
    try {
      const existingMessages = await firstValueFrom(this.chatService.messages$);
      console.log('🔍 initializeAIServiceWithCollectedData - Messages count:', existingMessages.length);
      console.log('🔍 initializeAIServiceWithCollectedData - Messages:', existingMessages);
      
      if (existingMessages.length === 0) {
        console.log('🔍 No existing messages - starting fresh conversation');
        // No messages, start fresh - only call startConversation, not sendMessage
        const conversationHistory: Array<{role: string, content: string, timestamp: string}> = [];
        const startResponse = await firstValueFrom(this.aiConversationService.startConversation(conversationHistory));
        
        // The startConversation should return the first question
        if (startResponse && startResponse.ai_response) {
          await this.chatService.addMessage(startResponse.ai_response);
          this.currentAIQuestion = {
            text: startResponse.ai_response,
            type: 'contextual',
            expectedFields: [],
            priority: 1,
            followUpHints: [],
            questionType: 'contextual',
            confidence: startResponse.confidence || 0.8
          };
        }
        return;
      }

      console.log('🔍 Found existing messages - initializing with conversation history');
      // Always initialize AI service with restored conversation history
      const conversationHistory: Array<{role: string, content: string, timestamp: string}> = existingMessages.map((msg: any) => ({
        role: msg.isBot ? 'assistant' : 'user',
        content: msg.content,
        timestamp: msg.timestamp?.toISOString ? msg.timestamp.toISOString() : msg.timestamp
      }));
      await firstValueFrom(this.aiConversationService.startConversation(conversationHistory));

      // --- Fix: Explicitly update local conversation state to match restored messages ---
      const contextState = {
        status: 'success',
        session_id: this.aiConversationService.getCurrentSessionId(),
        phase: this.currentPhase,
        collected_data: {}, // Optionally restore collected data if available
        conversation_history: conversationHistory,
        business_type: undefined,
        industry_focus: [],
        required_fields: []
      };
      this.aiConversationService.updateConversationState(contextState);
      // -------------------------------------------------------------------------------

      // Check phase and last message
      const lastMessage = existingMessages[existingMessages.length - 1];
      if (
        (this.currentPhase === ChatPhase.DATA_COLLECTION || this.currentPhase === ChatPhase.AI_GENERATION) &&
        lastMessage && !lastMessage.isBot
      ) {
        // Last message is from user, ask AI for next question
        const result = await this.aiConversationService.processUserResponse('');
        if (result && result.success && result.nextQuestion) {
          await this.chatService.addMessage(result.nextQuestion);
          this.currentAIQuestion = {
            text: result.nextQuestion,
            type: 'contextual',
            expectedFields: [],
            priority: 1,
            followUpHints: [],
            questionType: 'contextual',
            confidence: result.confidence || 0.8
          };
        }
      } else if (lastMessage && lastMessage.isBot) {
        // Last message is from bot, set currentAIQuestion for UI
        this.currentAIQuestion = {
          text: lastMessage.content,
          type: 'contextual',
          expectedFields: [],
          priority: 1,
          followUpHints: [],
          questionType: 'contextual',
          confidence: 0.8
        };
      }
    } catch (error) {
      console.error('AI service startConversation failed:', error);
    }
  }

  /**
   * Sync data from traditional chat service to AI service
   */
  private async syncDataToAIService(): Promise<void> {
    try {
      const supplierData = this.chatService.getSupplierData();
      
      // Get current AI conversation context
      const context = await firstValueFrom(this.aiConversationService.getConversationContext());
      
      if (context && context.collected_data) {
        // Check if we need to sync any missing data
        const supplierDataAny = supplierData as any;
        const missingInAI = Object.keys(supplierData).filter(key => 
          supplierDataAny[key] && !context.collected_data[key]
        );
        
        if (missingInAI.length > 0) {
          const dataToSync = missingInAI.map(key => `${key}: ${supplierDataAny[key]}`).join(', ');
          await firstValueFrom(this.aiConversationService.sendMessage(dataToSync, false));
        }
      }
      
    } catch (error) {
      // Error handling is done silently
    }
  }

  // Modify the selectColorTheme method to store theme preference
  async selectColorTheme(theme: string): Promise<void> {
    this.chatService.selectColorTheme(theme);
    await this.indexedDBService.setUserTheme(this.chatService.getCompanyGuid(), theme);
  }

  async deployWebsite(): Promise<void> {
    this.deploymentStartTime = Date.now();
    await this.chatService.deployWebsite();
  }

  allQuestionsCompleted(): boolean {
    // Use the new comprehensive data collection check from chat service
    return this.chatService.isDataCollectionComplete();
  }

  toggleMinimize(): void {
    this.chatService.toggleMinimize();
  }

  private scrollToBottom(): void {
    if (this.chatMessagesElement) {
      const element = this.chatMessagesElement.nativeElement;
      setTimeout(() => {
        // Check if scrollTo method exists before calling it
        if (typeof element.scrollTo === 'function') {
          // Force scroll to bottom with smooth behavior
          element.scrollTo({
            top: element.scrollHeight,
            behavior: 'smooth'
          });
        } else {
          // Fallback for elements that don't support scrollTo
          element.scrollTop = element.scrollHeight;
        }
        
        // Additional fallback to ensure we're at the bottom
        if (element.scrollTop !== element.scrollHeight - element.clientHeight) {
          element.scrollTop = element.scrollHeight;
        }
      }, 0);
    }
  }

  getCurrentTime(): string {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }



  trackMessage(index: number, message: ChatMessage): string {
    return message.id;
  }

  formatTime(timestamp: Date): string {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // Form handling methods
  isCurrentQuestionForm(): boolean {
    const currentQuestion = this.getCurrentQuestion();
    return currentQuestion?.type === 'form';
  }

  getCurrentQuestion(): any {
    // In AI-driven flow, we don't use traditional step-based questions
    // Return null since questions are now handled by AI conversation service
    return null;
  }

  getCurrentFormFields(): any[] {
    const currentQuestion = this.getCurrentQuestion();
    return currentQuestion?.fields || [];
  }

  getCurrentQuestionPlaceholder(): string {
    const currentQuestion = this.getCurrentQuestion();
    return currentQuestion?.placeholder || 'Type your answer here...';
  }

  isCurrentQuestionOptional(): boolean {
    const currentQuestion = this.getCurrentQuestion();
    return currentQuestion?.optional === true;
  }

  async skipCurrentQuestion(): Promise<void> {
    const currentQuestion = this.getCurrentQuestion();
    if (currentQuestion?.optional) {
      // Use empty input to trigger skip behavior in chat service
      await this.chatService.handleUserInput('');
      // Clear any existing input
      this.userInput = '';
      // Clear file selection if it's a file question
      this.clearFileSelection();
    }
  }

  // File upload methods
  isCurrentQuestionFile(): boolean {
    const currentQuestion = this.getCurrentQuestion();
    return currentQuestion?.type === 'file';
  }

  isCurrentQuestionMultiselect(): boolean {
    const question = this.getCurrentQuestion();
    return question?.type === 'multiselect';
  }

  // Color selection is now handled in preview component - this method is no longer needed
  isCurrentQuestionColorSelection(): boolean {
    return false;
  }

  getCurrentFileTypes(): string {
    const currentQuestion = this.getCurrentQuestion();
    return currentQuestion?.fileTypes?.join(',') || 'image/*';
  }

  getCurrentFileHint(): string {
    const currentQuestion = this.getCurrentQuestion();
    if (currentQuestion?.fileTypes?.includes('image/jpeg') && currentQuestion?.fileTypes?.includes('image/png')) {
      return 'JPEG or PNG files, max 5MB';
    }
    return 'Select a file to upload';
  }

  triggerFileInput(): void {
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelection(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFileSelection(event.dataTransfer.files[0]);
    }
  }

  private handleFileSelection(file: File): void {
    const currentQuestion = this.getCurrentQuestion();
    
    // Traditional file upload validation
    const validation = currentQuestion?.fileValidation?.(file);
    if (validation && !validation.valid) {
      // Show error message
      this.chatService.addMessage(`❌ ${validation.message}`);
      return;
    }

    this.selectedFile = file;
    this.generateFilePreview(file);
  }

  private generateFilePreview(file: File): void {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.filePreviewUrl = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeFile(event: Event): void {
    event.stopPropagation();
    this.clearFileSelection();
  }

  private clearFileSelection(): void {
    this.selectedFile = null;
    this.filePreviewUrl = null;
    this.isDragOver = false;
  }

  async uploadFile(): Promise<void> {
    if (!this.selectedFile) return;

    // Convert file to data URL for storage
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target?.result as string;
      
      // Handle the file upload similar to text input
      await this.chatService.handleFileUpload(this.selectedFile!, dataUrl);
      
      // Clear file selection
      this.clearFileSelection();
    };
    reader.readAsDataURL(this.selectedFile);
  }

  /**
   * Handle logo upload in AI-driven flow
   */
  async uploadLogoInAIFlow(): Promise<void> {
    if (!this.selectedFile) return;

    // Convert file to data URL for storage
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target?.result as string;
      
      // Add user message showing file upload
      await this.chatService.addMessage(`📎 ${this.selectedFile!.name} (${this.formatFileSize(this.selectedFile!.size)})`, false);
      
      // Store the logo data
      const currentData = this.chatService.getSupplierData();
      const updatedData = {
        ...currentData,
        logoURL: dataUrl
      };
      (this.chatService as any).supplierDataSubject.next(updatedData);
      
      // Store supplier data to IndexedDB
      await this.indexedDBService.setUserSupplierData(this.getCompanyGuid(), updatedData);
      
      // Update preview
      await (this.chatService as any).updatePreview();
      
      // Show success message
      await this.chatService.addMessage('✅ Perfect! I\'ve uploaded your logo and updated your website preview.');
      
      // Clear file selection
      this.clearFileSelection();
    };
    reader.readAsDataURL(this.selectedFile);
  }

  /**
   * Skip logo upload in AI-driven flow
   */
  async skipLogoUpload(): Promise<void> {
    // Add message indicating skip
    await this.chatService.addMessage('No problem! We\'ll use a default logo placeholder for now. You can always update it later.', false);
    await this.chatService.addMessage('✅ Great! Your website is ready to be created.');
    
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getInputType(fieldName: string): string {
    switch (fieldName) {
      case 'contactEmail':
        return 'email';
      case 'phoneNumber':
        return 'tel';
      case 'websiteURL':
      case 'logoURL':
        return 'url';
      default:
        return 'text';
    }
  }

  // Check if field is capabilities multiselect
  isCapabilitiesField(fieldName: string): boolean {
    return fieldName === 'capabilities';
  }

  // Check if field is equipment textarea
  isEquipmentField(fieldName: string): boolean {
    return fieldName === 'equipment';
  }

  // Get selected capabilities as array
  getSelectedCapabilities(): string[] {
    const capabilities = this.formData['capabilities'];
    if (typeof capabilities === 'string' && capabilities) {
      return capabilities.split(', ').map(c => c.trim()).filter(c => c.length > 0);
    }
    return Array.isArray(capabilities) ? capabilities : [];
  }

  // Toggle capability selection
  toggleCapability(capability: string): void {
    const selected = this.getSelectedCapabilities();
    const index = selected.indexOf(capability);
    
    if (index === -1) {
      selected.push(capability);
    } else {
      selected.splice(index, 1);
    }
    
    this.formData['capabilities'] = selected.join(', ');
  }

  // Check if capability is selected
  isCapabilitySelected(capability: string): boolean {
    return this.getSelectedCapabilities().includes(capability);
  }

  // Methods for single question multiselect (capabilities)
  getSelectedCapabilitiesForQuestion(): string[] {
    return this.selectedCapabilitiesForQuestion;
  }

  isCapabilitySelectedForQuestion(capability: string): boolean {
    return this.selectedCapabilitiesForQuestion.includes(capability);
  }

  toggleCapabilityForQuestion(capability: string): void {
    const index = this.selectedCapabilitiesForQuestion.indexOf(capability);
    
    if (index === -1) {
      this.selectedCapabilitiesForQuestion.push(capability);
    } else {
      this.selectedCapabilitiesForQuestion.splice(index, 1);
    }
  }

  async submitCapabilities(): Promise<void> {
    if (this.selectedCapabilitiesForQuestion.length === 0) return;

    const capabilitiesString = this.selectedCapabilitiesForQuestion.join(', ');
    
    // Handle the capabilities submission similar to text input
    await this.chatService.handleUserInput(capabilitiesString);
    
    // Clear the selection for next time
    this.selectedCapabilitiesForQuestion = [];
  }

  async submitForm(): Promise<void> {
    if (!this.isFormValid()) return;

    // Create a copy of the form data before submission
    const formDataCopy = { ...this.formData };

    // Handle form submission through chat service
    const success = await this.chatService.handleFormSubmission(formDataCopy);
    
    // Only clear form data if submission was successful
    if (success) {
      this.formData = {};
    }
    
    // Ensure scrolling works after form submission
    setTimeout(() => this.scrollToBottom(), 100);
  }

  isFormValid(): boolean {
    const currentQuestion = this.getCurrentQuestion();
    if (!currentQuestion || !currentQuestion.fields) return false;

    // Check if all required fields have values
    for (const field of currentQuestion.fields) {
      if (!field.optional) {
        const value = this.formData[field.field];
        
        // Special handling for capabilities multiselect
        if (field.field === 'capabilities') {
          const selectedCaps = this.getSelectedCapabilities();
          if (selectedCaps.length === 0) {
            return false;
          }
        } else {
          // Regular field validation
          if (!value || value.trim() === '') {
            return false;
          }
        }
      }
    }
    return true;
  }

  getDeploymentStatus(): string {
    return 'Deploying your website...';
  }

  getDeploymentProgress(): number {
    // Calculate progress based on deployment time elapsed
    // This is a visual estimate for user feedback
    if (!this.isDeploying) return 0;
    
    // Progress timing aligned with corrected message timeline
    const startTime = Date.now();
    const elapsed = (startTime - (this.deploymentStartTime || startTime)) / 1000;
    
    // Progress stages matching the message timeline:
    // 0-30s: 0-15% (domain setup)
    // 30-90s: 15-35% (upload content)
    // 90-150s: 35-55% (configure website)
    // 150-210s: 55-70% (testing)
    // 210-270s: 70-80% (taking longer)
    // 270-330s: 80-90% (almost ready)
    // 330s+: 90-95% (accessibility checking)
    
    if (elapsed < 30) return Math.min(15, (elapsed / 30) * 15);
    if (elapsed < 90) return 15 + Math.min(20, ((elapsed - 30) / 60) * 20);
    if (elapsed < 150) return 35 + Math.min(20, ((elapsed - 90) / 60) * 20);
    if (elapsed < 210) return 55 + Math.min(15, ((elapsed - 150) / 60) * 15);
    if (elapsed < 270) return 70 + Math.min(10, ((elapsed - 210) / 60) * 10);
    if (elapsed < 330) return 80 + Math.min(10, ((elapsed - 270) / 60) * 10);
    return Math.min(95, 90 + ((elapsed - 330) / 90) * 5);
  }

  private deploymentStartTime?: number;



  // AI Content Phase Helper Methods
  isDataCollectionPhase(): boolean {
    return this.currentPhase === ChatPhase.DATA_COLLECTION;
  }

  isAIGenerationPhase(): boolean {
    return this.currentPhase === ChatPhase.AI_GENERATION;
  }



  isColorSelectionPhase(): boolean {
    return false; // Color selection is now handled in preview component
  }

  isLogoUploadPhase(): boolean {
    return false; // Logo upload is now handled in edit mode
  }

  isDeploymentPhase(): boolean {
    return this.currentPhase === ChatPhase.DEPLOYMENT;
  }

  isCompletedPhase(): boolean {
    return this.currentPhase === ChatPhase.COMPLETED;
  }

  showProgressIndicator(): boolean {
    // In AI-driven flow, we don't show step-based progress indicators
    return false;
  }

  getPhaseDescription(): string {
    switch (this.currentPhase) {
      case ChatPhase.DATA_COLLECTION:
        return 'Collecting Information';
      case ChatPhase.AI_GENERATION:
        return 'Generating the content for your website...';

      case ChatPhase.DEPLOYMENT:
        if (!this.isDeploying) return 'Ready to Deploy';
        
        // Dynamic deployment messages for progress bar only
        const progress = this.getDeploymentProgress();
        if (progress < 15) return 'Setting up your web address...';
        if (progress < 35) return 'Uploading your content and images...';
        if (progress < 55) return 'Configuring your website settings...';
        if (progress < 70) return 'Testing everything to make sure it works perfectly...';
        if (progress < 80) return 'Taking longer than usual - probably just extra thorough testing!';
        if (progress < 90) return 'Almost there! Your website should be live any moment now...';
        return 'Checking website accessibility...';
      case ChatPhase.COMPLETED:
        return 'Your website is live!';
      default:
        return '';
    }
  }

  private progressTimer?: any;

  private startProgressTimer(): void {
    this.stopProgressTimer(); // Clear any existing timer
    this.progressTimer = setInterval(() => {
      // Force change detection to update progress bar
      // The getDeploymentProgress() method will calculate current progress
    }, 1000); // Update every second
  }

  private stopProgressTimer(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = undefined;
    }
  }

  // Step 2.3: AI Question Helper Methods
  
  isCurrentQuestionAI(): boolean {
    return this.isAIModeEnabled && this.aiControlEnabled && !!this.currentAIQuestion;
  }

  getAIQuestionText(): string {
    return this.currentAIQuestion?.text || '';
  }

  getAIFollowUpHints(): string[] {
    return this.currentAIQuestion?.followUpHints || [];
  }

  getAIBusinessContext(): any {
    return this.currentAIQuestion?.businessContext || null;
  }

  getAIConfidenceLevel(): number {
    return this.currentAIQuestion?.confidence || 0;
  }

  getDataCompleteness(): number {
    return this.contentPreview?.data_completeness || 0;
  }

  getContentPreviewHeadline(): string {
    return '';
  }

  getContentPreviewConfidence(): number {
    return 0;
  }

  getDetectedBusinessType(): string {
    return '';
  }

  getDetectedIndustries(): string[] {
    return [];
  }

  shouldShowBusinessExpertise(): boolean {
    return false;
  }

  // AI Mode toggle removed - AI is now permanently enabled
  // AI control is managed through the existing aiControlEnabled property

  async selectFollowUpHint(hint: string): Promise<void> {
    this.userInput = hint;
    await this.sendMessage();
  }

  // DEBUG METHODS
  shouldShowTraditionalInput(): boolean {
    return this.isDataCollectionPhase() && 
           !this.isCurrentQuestionForm() && 
           !this.isCurrentQuestionFile() && 
           !this.isCurrentQuestionMultiselect() && 
           !this.isCurrentQuestionColorSelection() && 
           !this.isDeploying && 
           !this.isGeneratingAIContent && 
           !this.isAIModeEnabled;
  }

  shouldShowAIInput(): boolean {
    // Show AI input during normal AI conversation OR during deployment phase for change requests
    return ((this.isAIModeEnabled && this.aiControlEnabled) || 
            (this.currentPhase === ChatPhase.DEPLOYMENT && !this.isDeploying)) && 
           !this.isGeneratingAIContent;
  }

  /**
   * Handle AI termination when all required data is collected
   */
  private async handleAITermination(): Promise<void> {
    this.aiControlEnabled = false;
    this.currentAIQuestion = null;
    this.chatService.disableAIControl();
  }

  /**
   * Handle AI content generation when data collection is complete
   */
  private async handleAIContentGeneration(): Promise<void> {
    
    // Disable AI control during content generation
    this.aiControlEnabled = false;
    this.currentAIQuestion = null;
    this.chatService.disableAIControl();
    
    // Trigger AI content generation - the existing subscription to aiContentGeneration$ 
    // will handle the transition to deployment phase after completion
    try {
      await (this.chatService as any).startAIContentGeneration();
    } catch (error) {
      console.error('❌ AI content generation failed to start:', error);
      // Fallback: transition to deployment anyway
      this.currentPhase = ChatPhase.DEPLOYMENT;
      (this.chatService as any).currentPhaseSubject.next(ChatPhase.DEPLOYMENT);
      
      await this.chatService.addMessage('There was an issue generating AI content, but your website is still ready to deploy with the information you provided.');
    }
  }

  /**
   * Extract structured data from conversation history and sync to chat service
   * NOTE: This method is now simplified since AI-driven extraction is handled in processChatCrewMessage
   */
  private async extractAndSyncConversationData(): Promise<void> {
    const allMessages = this.messages;
    const userMessages = allMessages.filter(msg => !msg.isBot);
      
    if (userMessages.length === 0) {
      return;
    }
      
    const allUserText = userMessages.map(msg => msg.content).join(' ');
    const extractedData: any = {};
      
    const emailMatch = allUserText.match(/[\w\.-]+@[\w\.-]+\.\w+/);
    if (emailMatch) {
      extractedData.contactEmail = emailMatch[0];
    }
          
    const phoneMatch = allUserText.match(/\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})/);
    if (phoneMatch) {
      extractedData.phoneNumber = phoneMatch[0];
    }
    
    if (Object.keys(extractedData).length > 0) {
      const currentData = this.chatService.getSupplierData();
      const updatedData = { ...currentData, ...extractedData };
        
      (this.chatService as any).supplierDataSubject.next(updatedData);
      await (this.chatService as any).updatePreview();
    }
  }

  /**
   * Save the last 2 messages (bot question + user answer) to IndexedDB
   */
  private async saveChatMessages(): Promise<void> {
    try {
      const currentMessages = await firstValueFrom(this.chatService.messages$);
      
      // Only save if we have at least 2 messages
      if (currentMessages.length >= 2) {
        // Get the last 2 messages (bot question + user answer)
        const botQuestion = currentMessages[currentMessages.length - 2];
        const userAnswer = currentMessages[currentMessages.length - 1];
        
        // Save both messages to IndexedDB
        await this.indexedDBService.storeChatMessage(this.getCompanyGuid(), botQuestion);
        await this.indexedDBService.storeChatMessage(this.getCompanyGuid(), userAnswer);
      }
    } catch (error) {
      console.error('❌ Failed to save chat messages:', error);
    }
  }

  /**
   * Save the latest conversation pair (bot question + user answer) to IndexedDB
   * This method saves immediately after user response is added to chat
   */
  private async saveLatestChatPair(): Promise<void> {
    try {
      const currentMessages = await firstValueFrom(this.chatService.messages$);
      
      // Only save if we have exactly 2 messages (bot question + user answer)
      if (currentMessages.length >= 2) {
        // Get the last 2 messages (bot question + user answer)
        const botQuestion = currentMessages[currentMessages.length - 2];
        const userAnswer = currentMessages[currentMessages.length - 1];
        
        // Save both messages to IndexedDB
        await this.indexedDBService.storeChatMessage(this.getCompanyGuid(), botQuestion);
        await this.indexedDBService.storeChatMessage(this.getCompanyGuid(), userAnswer);
      }
    } catch (error) {
      console.error('❌ Failed to save latest chat pair:', error);
    }
  }

  /**
   * Utility method to add delay
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Process change request using comprehensive three-context Change Request Crew system
   */
  private async processChangeRequest(input: string): Promise<void> {
    try {
      // Add user message to chat
      await this.chatService.addMessage(input, false);
      
      // Save the latest conversation pair immediately after user response
      await this.saveLatestChatPair();
      
      // Show typing indicator
      this.isTyping = true;
      this.scrollToBottom();
      
      // Get current supplier data (prioritized automatically by backend)
      const currentSupplierData = this.chatService.getSupplierData();
      
      // Get current semantic content from preview service
      const currentSemanticContent = this.previewService.getCurrentSemanticContent();
      

      
      // Process change request using comprehensive three-context service
      const changeResponse = await this.changeRequestService.processChangeRequestComprehensive(
        input, 
        currentSupplierData, 
        currentSemanticContent
      );
      
      // Hide typing indicator
      this.isTyping = false;
      
      if (changeResponse.status === 'success') {

        
        // Apply comprehensive changes to both supplier data and semantic content
        const { updatedSupplierData, updatedSemanticContent } = this.changeRequestService.applyComprehensiveChanges(
          currentSupplierData,
          currentSemanticContent,
          changeResponse
        );
        
        // Update the chat service's supplier data
        (this.chatService as any).supplierDataSubject.next(updatedSupplierData);
        
        // Store supplier data to IndexedDB
        await this.indexedDBService.setUserSupplierData(this.getCompanyGuid(), updatedSupplierData);
        
        // Update preview with new supplier data (placeholder replacements)
        await (this.chatService as any).updatePreview();
        
        // Apply updated semantic content if there are semantic changes
        if (Object.keys(changeResponse.updatedSemanticContent).length > 0) {
          await this.previewService.applyUpdatedSemanticContent(updatedSemanticContent);
        }
        
        // Add simple user-friendly confirmation message
        const simpleConfirmationMessage = "Perfect! I've updated the changes you requested. Your website has been updated accordingly.";
        await this.chatService.addMessage(simpleConfirmationMessage);
        

        
      } else if (changeResponse.status === 'clarification_needed') {
        // Handle clarification request
        const clarificationMessage = changeResponse.clarificationRequest || changeResponse.message;
        await this.chatService.addMessage(clarificationMessage);
        
      } else {
        // Handle error case
        const errorMessage = changeResponse.message || 'I had trouble processing that change. Please try again.';
        await this.chatService.addMessage(errorMessage);
        
        if (changeResponse.error) {
          console.error('❌ Change request error details:', changeResponse.error);
        }
      }
      
    } catch (error) {
      console.error('❌ Comprehensive change request processing failed:', error);
      this.isTyping = false;
      await this.chatService.addMessage('Sorry, I had trouble processing your change request. Please try again.');
    } finally {
      this.isProcessingMessage = false;
    }
  }

  /**
   * Get appropriate placeholder text for AI input
   */
  getAIInputPlaceholder(): string {
    if (this.currentPhase === ChatPhase.DEPLOYMENT) {
      return 'Ask me anything or request changes...';
    }
    return 'Tell me about your business...';
  }

  private extractJsonFromResponse(response: string): string {
    const trimmed = response.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      return trimmed;
    }
    
    const jsonMatch = trimmed.match(/\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/);
    if (jsonMatch) {
      return jsonMatch[0];
    }
    
    const codeBlockMatch = trimmed.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
    if (codeBlockMatch) {
      return codeBlockMatch[1].trim();
    }
    
    return response;
  }

  private async initializeChat(): Promise<void> {
    try {
      // Load previous chat messages to determine if we have a restored session
      const savedMessages = await this.indexedDBService.getChatMessages(this.getCompanyGuid());
      const savedTheme = await this.indexedDBService.getUserTheme(this.getCompanyGuid());
      this.hasRestoredSession = savedMessages.length > 0 && savedTheme !== null;
      
      // Let ChatService handle everything - it already has restored session logic built-in
      await this.chatService.initializeChat();
      
      // For restored sessions, just update our component state
      if (this.hasRestoredSession) {
        
        // Theme state is now handled in preview component
        
        // Enable AI mode since we're restoring a session
        this.isAIModeEnabled = true;
        this.aiControlEnabled = true;
        
      } else {
        // For new sessions, AI control is enabled immediately by ChatService
        this.isAIModeEnabled = true;
        this.aiControlEnabled = true;
      }
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      this.hasRestoredSession = false;
      await this.chatService.initializeChat();
    }
  }

  /**
   * Clear all IndexedDB data and restart the chat session
   */
  async clearDataAndRestart(): Promise<void> {
    try {
      // Show confirmation dialog
      const confirmed = confirm('Are you sure you want to clear all saved data and start fresh? This action cannot be undone.');
      
      if (!confirmed) {
        return;
      }

      // Show loading state
      this.isChatLoading = true;

      // Clear all IndexedDB data FIRST
      await this.indexedDBService.clearAll();

      // Force clear messages immediately from UI
      this.messages = [];
      
      // Reset ALL component state to initial values
      this.hasRestoredSession = false;
      this.isAIModeEnabled = false;
      this.aiControlEnabled = false;
      this.currentAIQuestion = null;
      this.isTyping = false;
      this.currentPhase = ChatPhase.DATA_COLLECTION;
      this.userInput = '';
      this.formData = {};
      this.selectedFile = null;
      this.filePreviewUrl = null;
      this.isDragOver = false;
      this.isDeploying = false;
      this.isGeneratingAIContent = false;
      this.selectedCapabilitiesForQuestion = [];
      this.contentPreview = null;
      this.isProcessingAI = false;
      this.aiConfidence = 0;
      this.showFollowUpHints = false;
      this.isMinimized = false;

      // Reset chat service state completely
      this.chatService.resetToInitialState();
      
      // Force reload by waiting a bit then reinitializing
      await this.sleep(500);
      
      // Reinitialize chat as completely fresh session (bypass restoration logic)
      await this.chatService.forceInitializeChat();

      // Hide loading state
      this.isChatLoading = false;
    } catch (error) {
      console.error('❌ Failed to clear data and restart:', error);
      this.isChatLoading = false;
      alert('Failed to clear data. Please try again.');
    }
  }
}
